import React, { useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { HelpPageAri } from '@atlassian/ari/help';

import messages from '../messages';
import type { siteNavigation_AtlassianHubAppShell$key } from './__generated__/siteNavigation_AtlassianHubAppShell.graphql';
import type { HelpCenterFolder, HelpCenterPage, HelpCenterSiteTreeNode } from './types';
import { useSiteNavigationOverflow } from './use-site-navigation-overflow';

const EMPTY_SITE_NAVIGATION_ITEMS: HelpCenterSiteTreeNode[] = [];

const styles = cssMap({
	// Hug the packed folders so the filled nav row can centre the whole group.
	// Must lay out as a row to match the hidden measurement row it is packed against.
	centerSlot: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		minWidth: 0,
		position: 'relative',
	},
	navItemLabel: {
		display: 'block',
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	// Mirrors the `subtle` appearance of @atlaskit/button so the packed folders
	// match the sibling nav triggers. The button cannot be used directly because
	// it drops the `style` prop that carries the hub's themed text colour.
	trigger: {
		display: 'inline-flex',
		boxSizing: 'border-box',
		alignItems: 'center',
		columnGap: token('space.050'),
		height: '2rem',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.medium'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	triggerSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	measurementViewport: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		overflowX: 'hidden',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
	measurementRow: {
		display: 'flex',
		gap: token('space.050'),
		width: 'max-content',
	},
});

const NavItemLabel = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
	<Box as="span" xcss={styles.navItemLabel}>
		{children}
	</Box>
);

type NavTriggerProps = Omit<React.ComponentProps<typeof Pressable>, 'children' | 'xcss'> & {
	children: React.ReactNode;
	iconLabel?: string;
	isSelected?: boolean;
	showIcon?: boolean;
	textColor?: string;
	truncate?: boolean;
};

const NavTrigger = React.forwardRef<HTMLButtonElement, NavTriggerProps>(function NavTrigger(
	{
		children,
		iconLabel = '',
		isSelected = false,
		showIcon = true,
		textColor,
		truncate = false,
		...props
	},
	ref,
) {
	return (
		<Pressable
			{...props}
			ref={ref}
			style={{ color: isSelected ? undefined : textColor }}
			xcss={cx(styles.trigger, isSelected && styles.triggerSelected)}
		>
			{truncate ? <NavItemLabel>{children}</NavItemLabel> : children}
			{showIcon && <ChevronDownIcon color="currentColor" label={iconLabel} />}
		</Pressable>
	);
});

const getFolderPages = (folder: HelpCenterFolder): HelpCenterPage[] =>
	folder.contents.nodes.filter(
		(node): node is HelpCenterPage => node.__typename === 'HelpCenterPage',
	);

const getPageId = (page: HelpCenterPage): string | null => {
	try {
		return HelpPageAri.parse(page.id).pageId;
	} catch {
		return null;
	}
};

type PageItemProps = {
	page: HelpCenterPage;
};

const PageItem = ({ page }: PageItemProps) => {
	const { push } = useRouterActions();
	const pageId = getPageId(page);

	if (!pageId) {
		return null;
	}

	return (
		<DropdownItem onClick={() => push(`/page/${pageId}`)}>
			<NavItemLabel>{page.name?.default ?? ''}</NavItemLabel>
		</DropdownItem>
	);
};

const FolderMenuItems = ({ pages }: { pages: HelpCenterPage[] }) => (
	<DropdownItemGroup>
		{pages.map((page) => (
			<PageItem key={page.id} page={page} />
		))}
	</DropdownItemGroup>
);

type FolderMenuProps = {
	folder: HelpCenterFolder;
	textColor?: string;
};

const PromotedFolderMenu = ({ folder, textColor }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="bottom-start"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<NavTrigger
					{...props}
					iconLabel={formatMessage(messages.downIcon)}
					ref={triggerRef}
					textColor={textColor}
					truncate
				>
					{folder.name?.default ?? ''}
				</NavTrigger>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const PromotedPage = ({ page, textColor }: { page: HelpCenterPage; textColor?: string }) => {
	const { push } = useRouterActions();
	const pageId = getPageId(page);

	if (!pageId) {
		return null;
	}

	return (
		<NavTrigger
			onClick={() => push(`/page/${pageId}`)}
			showIcon={false}
			textColor={textColor}
			truncate
		>
			{page.name?.default ?? ''}
		</NavTrigger>
	);
};

const OverflowFolderMenu = ({ folder }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="right-start"
			shouldFlip
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<DropdownItem
					{...props}
					elemAfter={
						<ChevronRightIcon label={formatMessage(messages.openFolderSubmenu)} size="small" />
					}
					ref={triggerRef}
				>
					<NavItemLabel>{folder.name?.default ?? ''}</NavItemLabel>
				</DropdownItem>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowItem = ({ item }: { item: HelpCenterSiteTreeNode }) =>
	item.__typename === 'HelpCenterPage' ? (
		<PageItem page={item} />
	) : (
		<OverflowFolderMenu folder={item} />
	);

export const SiteNavigationMenus = ({
	overflowItems,
	promotedItems,
	textColor,
}: {
	overflowItems: HelpCenterSiteTreeNode[];
	promotedItems: HelpCenterSiteTreeNode[];
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (promotedItems.length === 0 && overflowItems.length === 0) {
		return null;
	}

	return (
		<>
			{promotedItems.map((item) =>
				item.__typename === 'HelpCenterFolder' ? (
					<PromotedFolderMenu key={item.id} folder={item} textColor={textColor} />
				) : (
					<PromotedPage key={item.id} page={item} textColor={textColor} />
				),
			)}
			{overflowItems.length > 0 && (
				<DropdownMenu
					shouldRenderToParent
					trigger={({ triggerRef, ...props }) => (
						<NavTrigger
							{...props}
							iconLabel={formatMessage(messages.downIcon)}
							ref={triggerRef}
							testId="site-navigation-more"
							textColor={textColor}
						>
							{formatMessage(messages.more)}
						</NavTrigger>
					)}
				>
					<DropdownItemGroup>
						{overflowItems.map((item) => (
							<OverflowItem key={item.id} item={item} />
						))}
					</DropdownItemGroup>
				</DropdownMenu>
			)}
		</>
	);
};

export const SiteNavigation = ({
	queryRef,
	textColor,
}: {
	queryRef: siteNavigation_AtlassianHubAppShell$key | null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const slotRef = useRef<HTMLDivElement>(null);
	const measurementRowRef = useRef<HTMLDivElement>(null);
	const data = useFragment(
		graphql`
			fragment siteNavigation_AtlassianHubAppShell on HelpCenter {
				siteTree {
					root {
						contents {
							nodes {
								__typename
								... on HelpCenterPage {
									id
									name {
										default
									}
								}
								... on HelpCenterFolder {
									id
									name {
										default
									}
									contents {
										nodes {
											__typename
											... on HelpCenterPage {
												id
												name {
													default
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);
	const nodes = data?.siteTree?.root?.contents.nodes;
	const items = useMemo(
		() =>
			nodes?.filter(
				(node): node is HelpCenterSiteTreeNode =>
					node.__typename === 'HelpCenterPage' || node.__typename === 'HelpCenterFolder',
			) ?? EMPTY_SITE_NAVIGATION_ITEMS,
		[nodes],
	);
	const { overflowItems, promotedItems } = useSiteNavigationOverflow({
		items,
		measurementRowRef,
		slotRef,
	});
	return (
		<Box ref={slotRef} testId="nav-links-center-slot" xcss={styles.centerSlot}>
			<SiteNavigationMenus
				overflowItems={overflowItems}
				promotedItems={promotedItems}
				textColor={textColor}
			/>
			<Box
				aria-hidden="true"
				testId="site-navigation-measurement-viewport"
				xcss={styles.measurementViewport}
			>
				<Box
					aria-hidden="true"
					ref={measurementRowRef}
					testId="site-navigation-measurement-row"
					xcss={styles.measurementRow}
				>
					{items.map((item) => (
						<Box data-site-navigation-item-id={item.id} key={item.id}>
							<NavTrigger iconLabel="" tabIndex={-1} textColor={textColor} truncate>
								{item.name?.default}
							</NavTrigger>
						</Box>
					))}
					<Box data-site-navigation-more-item>
						<NavTrigger iconLabel="" tabIndex={-1} textColor={textColor}>
							{formatMessage(messages.more)}
						</NavTrigger>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
